import { AxiosError, AxiosRequestConfig } from 'axios';
import { ClientError, ClientErrorCode, sleep } from '~/shared';
import { IErrorDetails, IRpcResponse } from '../backendServiceTypes';
import { Requester } from '../Requester';

export class RestTransport {
	private requester: Requester;

	private backendUrl: string | undefined;
	private token: string | undefined;

	private REST_REQ_TIMEOUT_MS = 10_000;

	constructor() {
		this.requester = new Requester();
	}

	public setBackendUrl(url: string): void {
		this.backendUrl = url;
	}

	public setToken(token: string): void {
		this.token = token;
	}

	private async getEndpointUrl(methodName: string): Promise<string> {
		while (!this.backendUrl) {
			await sleep(10);
		}

		const url =
			this.backendUrl + '/api/client/rpc/' + methodName.replace(':', '/');
		return url;
	}

	private getBearer(): string | undefined {
		if (!this.token) {
			return undefined;
		}

		return 'Bearer ' + this.token;
	}

	public async invoke<T, P = unknown>(
		methodName: string,
		payload?: P,
		shouldRetry = true,
	): Promise<T> {
		const url = await this.getEndpointUrl(methodName);
		try {
			const config: AxiosRequestConfig = {
				url,
				method: 'POST',
				headers: {
					Authorization: this.getBearer(),
					'Content-Type': 'application/json',
				},
				data: JSON.stringify(payload),
				timeout: this.REST_REQ_TIMEOUT_MS,
			};

			const response = await this.requester.request<IRpcResponse<T>, P>(
				config,
				shouldRetry,
			);
			if (response.data.success) {
				return response.data.data as T;
			} else {
				throw this.mapError(response.data.error!);
			}
		} catch (error) {
			if (error instanceof AxiosError) {
				const responseData = error.response?.data as
					| { message?: string }
					| undefined;

				if (responseData?.message) {
					console.error(`Api Request error. ${responseData.message}`);
				}

				throw new ClientError(ClientErrorCode.InternalError, {
					method: methodName,
					message: error.message,
					code: error.code,
				});
			}

			throw error;
		}
	}

	private mapError(err: IErrorDetails): Error {
		if (err.message) console.error(err.message);
		if (err.stack && err.stack.length > 0) {
			console.error(err.stack.join('\n'));
		}

		if (err.code !== undefined) {
			return new ClientError(err.code, err.params);
		}

		const genericError = new Error(err.message);
		if (err.stack) {
			genericError.stack = err.stack.join('\n');
		}

		return genericError;
	}
}
